import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ABILITIES, ABILITY_NAMES, SKILLS, SKILL_KEYS, type SkillKey } from '@shared/rules/constants'
import { ApiError, api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { ImportError, readSheetPdf, type ImportedCharacter, type ImportResult } from '@/lib/pdf/importSheet'
import { matchSpells, toImportPayload } from '@/lib/pdf/toImportPayload'
import {
  Badge, Button, ErrorNote, Field, Input, NumberField, PageLoader, Panel, SectionHeading, Select, Textarea,
} from '@/components/ui'
import { cn } from '@/lib/cn'

const SOURCE_LABEL: Record<ImportResult['source'], string> = {
  'official-2024': 'Official 2024 sheet',
  'official-2014': 'Official 2014 sheet',
  unrecognised: 'Unrecognised layout',
}

export default function ImportCharacter() {
  const { campaignId = '' } = useParams()
  const navigate = useNavigate()
  const { data: campaign, loading } = useAsync(() => api.getCampaign(campaignId), [campaignId])

  const [result, setResult] = useState<ImportResult | null>(null)
  const [character, setCharacter] = useState<ImportedCharacter | null>(null)
  const [assignTo, setAssignTo] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  if (loading) return <PageLoader />

  const isDm = campaign?.myRole === 'dm'
  const update = (patch: Partial<ImportedCharacter>) =>
    setCharacter((current) => (current ? { ...current, ...patch } : current))

  async function handleFile(file: File) {
    setError(null)
    setReading(true)
    try {
      const parsed = await readSheetPdf(await file.arrayBuffer())
      setResult(parsed)
      setCharacter(parsed.character)
      setAssignTo('')
    } catch (err) {
      setResult(null)
      setCharacter(null)
      setError(err instanceof ImportError || err instanceof Error ? err.message : 'Could not read that file')
    } finally {
      setReading(false)
    }
  }

  async function save() {
    if (!character) return
    setSaving(true)
    setError(null)
    try {
      const userId = !isDm ? undefined : assignTo === '' ? undefined : assignTo === '__none__' ? null : assignTo
      const res = await api.importCharacter(campaignId, toImportPayload(character, userId))
      navigate(`/characters/${res.character.id}`, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that character')
      setSaving(false)
    }
  }

  const spellMatch = character ? matchSpells(character.spellNames) : null

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-10">
      <header>
        <button type="button" onClick={() => navigate(`/campaigns/${campaignId}`)}
          className="text-sm text-ink-500 transition-colors hover:text-ink-300">
          ← Back to the campaign
        </button>
        <h1 className="mt-3 text-display">Import a character sheet</h1>
        <p className="mt-1.5 max-w-2xl text-[15px] leading-relaxed text-ink-300">
          Already have a filled-in PDF? Drop it here and we will read what we can, show you
          everything before anything is saved, and attach it to whichever player it belongs to.
        </p>
      </header>

      {!character && (
        <>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              const file = e.dataTransfer.files[0]
              if (file) void handleFile(file)
            }}
            className={cn(
              'rounded-[--radius-card] border-2 border-dashed p-10 text-center transition-colors',
              dragging ? 'border-gold-500 bg-gold-900/20' : 'border-ink-700 bg-ink-850/40',
            )}
          >
            <p className="font-display text-lg">Drop a PDF here</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-ink-400">
              It needs to be a <span className="text-ink-200">fillable</span> sheet — the official
              2024 or 2014 D&amp;D character sheet with the form fields still in it.
            </p>
            <Button variant="primary" className="mt-4" busy={reading}
              onClick={() => fileInput.current?.click()}>
              Choose a file
            </Button>
            <input ref={fileInput} type="file" accept="application/pdf,.pdf" className="sr-only"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f) }} />
          </div>

          {error && <ErrorNote>{error}</ErrorNote>}

          <Panel className="p-4 text-sm leading-relaxed text-ink-400">
            <SectionHeading>If it will not read</SectionHeading>
            <p>
              Sheets exported from D&amp;D Beyond, scanned, or printed to PDF are flattened — the
              text is baked into the page like a photograph, with no fields left to read. There is
              nothing to extract from those. Use the{' '}
              <button type="button" className="text-gold-400 underline"
                onClick={() => navigate(`/campaigns/${campaignId}/characters/new`)}>
                character builder
              </button>{' '}
              instead; with the sheet in front of you it takes a couple of minutes.
            </p>
          </Panel>
        </>
      )}

      {character && result && (
        <>
          <Panel className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-2.5">
              <Badge tone="moss">{SOURCE_LABEL[result.source]}</Badge>
              <span className="text-sm text-ink-400">
                {Object.keys(result.raw).length} fields read
              </span>
            </div>
            <Button size="sm" variant="ghost" onClick={() => { setCharacter(null); setResult(null) }}>
              Use a different file
            </Button>
          </Panel>

          {result.warnings.map((warning) => (
            <p key={warning} className="rounded-xl border border-gold-600/40 bg-gold-900/25 px-3 py-2 text-sm text-ink-300">
              {warning}
            </p>
          ))}

          {result.missing.length > 0 && (
            <p className="rounded-xl border border-gold-600/40 bg-gold-900/25 px-3 py-2 text-sm text-ink-300">
              Blank on the sheet, so please fill in: <span className="text-gold-400">{result.missing.join(', ')}</span>
            </p>
          )}

          {error && <ErrorNote>{error}</ErrorNote>}

          <Panel className="space-y-4 p-4">
            <SectionHeading>Who is this?</SectionHeading>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Name">
                {(id) => <Input id={id} value={character.name} maxLength={80}
                  onChange={(e) => update({ name: e.target.value })} />}
              </Field>
              <Field label="Class" hint="Match a 5.5e class name so bonuses calculate.">
                {(id) => <Input id={id} value={character.className} maxLength={60}
                  onChange={(e) => update({ className: e.target.value })} />}
              </Field>
              <Field label="Level">
                {(id) => <NumberField id={id} min={1} max={20} fallback={1} value={character.level}
                  onChange={(level) => update({ level })} className="w-full" />}
              </Field>
              <Field label="Species">
                {(id) => <Input id={id} value={character.species} maxLength={60}
                  onChange={(e) => update({ species: e.target.value })} />}
              </Field>
              <Field label="Background">
                {(id) => <Input id={id} value={character.background} maxLength={60}
                  onChange={(e) => update({ background: e.target.value })} />}
              </Field>
              <Field label="Alignment">
                {(id) => <Input id={id} value={character.alignment} maxLength={40}
                  onChange={(e) => update({ alignment: e.target.value })} />}
              </Field>
            </div>
          </Panel>

          <Panel className="space-y-4 p-4">
            <SectionHeading>Ability scores</SectionHeading>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
              {ABILITIES.map((ability) => (
                <div key={ability} className="stat-slab gap-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">{ability}</span>
                  <NumberField
                    min={1} max={30} fallback={10}
                    value={character.scores[ability]}
                    aria-label={ABILITY_NAMES[ability]}
                    onChange={(value) => update({ scores: { ...character.scores, [ability]: value } })}
                    className="w-16"
                  />
                </div>
              ))}
            </div>
          </Panel>

          <Panel className="space-y-4 p-4">
            <SectionHeading>Vitals</SectionHeading>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Max HP">
                {(id) => <NumberField id={id} min={1} max={999} fallback={1} value={character.hpMax}
                  onChange={(hpMax) => update({ hpMax })} className="w-full" />}
              </Field>
              <Field label="Current HP">
                {(id) => <NumberField id={id} min={-99} max={999} fallback={character.hpMax} value={character.hpCurrent}
                  onChange={(hpCurrent) => update({ hpCurrent })} className="w-full" />}
              </Field>
              <Field label="Armor Class">
                {(id) => <NumberField id={id} min={0} max={40} fallback={10} value={character.armorClass}
                  onChange={(armorClass) => update({ armorClass })} className="w-full" />}
              </Field>
              <Field label="Speed (ft)">
                {(id) => <NumberField id={id} min={0} max={200} step={5} fallback={30} value={character.speed}
                  onChange={(speed) => update({ speed })} className="w-full" />}
              </Field>
            </div>
          </Panel>

          <Panel className="space-y-4 p-4">
            <SectionHeading>Proficiencies read from the checkboxes</SectionHeading>
            <div>
              <p className="mb-2 text-xs uppercase tracking-wider text-ink-500">Saving throws</p>
              <div className="flex flex-wrap gap-2">
                {ABILITIES.map((ability) => {
                  const on = character.saveProficiencies.includes(ability)
                  return (
                    <Toggle key={ability} on={on} label={ABILITY_NAMES[ability]}
                      onClick={() => update({
                        saveProficiencies: on
                          ? character.saveProficiencies.filter((a) => a !== ability)
                          : [...character.saveProficiencies, ability],
                      })} />
                  )
                })}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs uppercase tracking-wider text-ink-500">Skills</p>
              <div className="flex flex-wrap gap-2">
                {SKILL_KEYS.map((skill) => {
                  const on = Boolean(character.skillProficiencies[skill])
                  return (
                    <Toggle key={skill} on={on} label={SKILLS[skill].name}
                      onClick={() => {
                        const next = { ...character.skillProficiencies }
                        if (on) delete next[skill as SkillKey]
                        else next[skill as SkillKey] = 'proficient'
                        update({ skillProficiencies: next })
                      }} />
                  )
                })}
              </div>
            </div>
          </Panel>

          {character.attacks.length > 0 && (
            <Panel className="p-4">
              <SectionHeading>Attacks</SectionHeading>
              <ul className="space-y-1.5">
                {character.attacks.map((attack, index) => (
                  <li key={`${attack.name}-${index}`} className="flex flex-wrap items-baseline gap-2 text-sm">
                    <span className="text-ink-100">{attack.name}</span>
                    <span className="font-mono text-gold-400">{attack.bonus}</span>
                    <span className="font-mono text-ink-400">{attack.damage}</span>
                    {attack.notes && <span className="text-[11px] text-ink-500">{attack.notes}</span>}
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {spellMatch && character.spellNames.length > 0 && (
            <Panel className="p-4">
              <SectionHeading>
                Spells{' '}
                <span className="normal-case tracking-normal text-ink-500">
                  — {spellMatch.cantrips.length + spellMatch.spells.length} of {character.spellNames.length} recognised
                </span>
              </SectionHeading>
              {spellMatch.unmatched.length > 0 && (
                <p className="text-sm text-ink-400">
                  These are not in the rules database yet, so they will be saved as a note on the
                  sheet rather than lost:{' '}
                  <span className="text-ink-300">{spellMatch.unmatched.join(', ')}</span>
                </p>
              )}
            </Panel>
          )}

          <Panel className="space-y-4 p-4">
            <SectionHeading>Text from the sheet</SectionHeading>
            {([
              ['featuresText', 'Class features'],
              ['speciesTraitsText', 'Species traits'],
              ['featsText', 'Feats'],
              ['equipmentText', 'Equipment'],
              ['languagesText', 'Languages'],
              ['appearance', 'Appearance'],
              ['backstory', 'Backstory'],
            ] as const).map(([key, label]) => (
              <Field key={key} label={label}>
                {(id) => (
                  <Textarea id={id} value={character[key]} className="min-h-20"
                    onChange={(e) => update({ [key]: e.target.value } as Partial<ImportedCharacter>)} />
                )}
              </Field>
            ))}
          </Panel>

          <Panel className="flex flex-wrap items-end justify-between gap-4 p-5">
            <div className="min-w-0">
              <h3 className="font-display text-lg">Ready to add them</h3>
              {isDm && campaign && (
                <div className="mt-3 w-64">
                  <Field label="Whose character is this?">
                    {(id) => (
                      <Select id={id} value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                        <option value="">Mine ({campaign.members.find((m) => m.role === 'dm')?.displayName})</option>
                        {campaign.members.filter((m) => m.role !== 'dm').map((member) => (
                          <option key={member.id} value={member.id}>{member.displayName}</option>
                        ))}
                        <option value="__none__">Nobody — a DM-run character</option>
                      </Select>
                    )}
                  </Field>
                </div>
              )}
            </div>
            <Button variant="primary" busy={saving} disabled={!character.name.trim()} onClick={save}>
              Add to the campaign
            </Button>
          </Panel>
        </>
      )}
    </div>
  )
}

function Toggle({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'rounded-full border px-2.5 py-1 text-[13px] transition-colors',
        on ? 'border-gold-500 bg-gold-900/40 text-gold-400' : 'border-ink-700 text-ink-500 hover:border-ink-500',
      )}
    >
      {label}
    </button>
  )
}
